import { api } from '../axios';
import {
  DiscountVoucherResponse,
  DiscountVoucherUI,
  CreateDiscountVoucherRequest,
  UpdateDiscountVoucherRequest,
  DiscountVoucherListQuery,
  PaginatedDiscountVoucherResponse,
  DiscountVoucherStatusCounts,
} from '@/types/discountVoucher';

export interface ApiResponse<T> {
  data: T;
  message: string;
  timestamp: string;
}

export const discountVoucherApi = {
  createDiscountVoucher: async (request: CreateDiscountVoucherRequest): Promise<DiscountVoucherResponse> => {
    const response = await api.post<ApiResponse<DiscountVoucherResponse>>('/discount-vouchers', request);
    return response.data.data;
  },

  getAllDiscountVouchers: async (query: DiscountVoucherListQuery = {}): Promise<PaginatedDiscountVoucherResponse> => {
    const params = new URLSearchParams();

    if (query.page) params.append('page', query.page.toString());
    if (query.page_size) params.append('page_size', query.page_size.toString());

    if (query.search) params.append('search', query.search);

    if (query.sort_by) params.append('sort_by', query.sort_by);
    if (query.sort_order) params.append('sort_order', query.sort_order);

    if (query.customer_id) params.append('customer_id', query.customer_id);
    if (query.status && query.status !== 'all') params.append('status', query.status);
    if (query.date_from) params.append('date_from', query.date_from);
    if (query.date_to) params.append('date_to', query.date_to);

    const response = await api.get<PaginatedDiscountVoucherResponse>(`/discount-vouchers?${params.toString()}`);
    return response.data;
  },

  getDiscountVoucher: async (code: string): Promise<DiscountVoucherResponse> => {
    const response = await api.get<ApiResponse<DiscountVoucherResponse>>(`/discount-vouchers/${code}`);
    return response.data.data;
  },

  updateDiscountVoucher: async (code: string, request: UpdateDiscountVoucherRequest): Promise<DiscountVoucherResponse> => {
    const response = await api.put<ApiResponse<DiscountVoucherResponse>>(`/discount-vouchers/${code}`, request);
    return response.data.data;
  },

  deleteDiscountVoucher: async (code: string): Promise<void> => {
    await api.delete(`/discount-vouchers/${code}`);
  },

  confirmDiscountVoucher: async (code: string): Promise<DiscountVoucherResponse> => {
    const response = await api.post<ApiResponse<DiscountVoucherResponse>>(`/discount-vouchers/${code}/confirm`);
    return response.data.data;
  },

  cancelDiscountVoucher: async (code: string): Promise<DiscountVoucherResponse> => {
    const response = await api.post<ApiResponse<DiscountVoucherResponse>>(`/discount-vouchers/${code}/cancel`);
    return response.data.data;
  },

  getStatusCounts: async (filters: Partial<DiscountVoucherListQuery> = {}): Promise<DiscountVoucherStatusCounts> => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await api.get<ApiResponse<Record<string, number>>>(`/discount-vouchers/status-counts?${params.toString()}`);
      const data = response.data.data || {};

      return {
        all: data.all || 0,
        draft: data.draft || 0,
        confirmed: data.confirmed || 0,
        cancelled: data.cancelled || 0,
      };
    } catch (error) {
      console.error('Error fetching discount voucher status counts:', error);
      return {
        all: 0,
        draft: 0,
        confirmed: 0,
        cancelled: 0,
      };
    }
  },
};

export const transformDiscountVoucherToUI = (voucher: DiscountVoucherResponse): DiscountVoucherUI => {
  return {
    ...voucher,
    invoice_count: voucher.invoice_discounts?.length ?? 0,
    status_display: voucher.current_status.status.toUpperCase(),
  };
};
